import { type FormEvent, useState } from 'react';
import { Check, CheckCircle2, ChevronDown, Send } from 'lucide-react';

import { NavyAtmosphere, ScrollReveal } from '@/components/motion';
import { Button, Input, Label, cn } from '@ibas/ui';
import { brand } from '@/config/brand';

export interface EnquiryFormProps {
  className?: string;
}

/** Shared field styling — mirrors packages/ui/src/components/input.tsx exactly
 * so the native <select> and <textarea> read as the same control family as
 * the Input primitive (same border, focus ring, sizing and disabled states). */
const FIELD_CLASSES = cn(
  'flex w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors outline-none',
  'placeholder:text-muted-foreground',
  'focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40',
  'aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/30',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'md:text-sm',
);

const PROGRAMME_OPTIONS = [
  'Flexi-MBA',
  'Flexi-DBA',
  'MBA (on-site)',
  'BBA (on-site)',
  'Diploma programme',
  "Master's programme",
  'Not sure yet',
];

const MODE_OPTIONS = ['Online', 'On-site', 'No preference'];

/** Small marked-up label with the gold required asterisk. */
function RequiredMark() {
  return (
    <span aria-hidden="true" className="text-brand-gold">
      {' '}
      *
    </span>
  );
}

/**
 * EnquiryForm — the pre-footer "request information" section, redesigned as a
 * single-screen (min-h/items-center) split: a navy panel carrying the invitation
 * and the brand promise sits beside a raised white card holding a compact,
 * accessible form (native <select>/<textarea> hand-styled to match the Input
 * primitive). Fields pair up in a two-column grid so the whole exchange reads at
 * a glance without scrolling.
 *
 * Motion intent: the panel copy and the form card rise in on scroll-in via the
 * house ScrollReveal (the fields themselves in a gentle stagger), over a shared
 * navy atmosphere that ties this closing beat to the hero and statement screens.
 * Under reduced motion every element renders in its final, fully visible
 * position immediately. On submit (a local-state placeholder — no network call
 * is wired yet) the card swaps to a confirmation state.
 */
export function EnquiryForm({ className }: EnquiryFormProps) {
  const [submitted, setSubmitted] = useState(false);

  // Placeholder handler — IBAS has no backend/endpoint wired for this form
  // yet. This only demonstrates the success state locally; nothing is sent
  // over the network.
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
  }

  return (
    <section
      aria-labelledby="enquiry-form-heading"
      className={cn(
        'relative isolate flex min-h-[100svh] items-center overflow-hidden bg-brand-navy px-6 py-12 lg:px-16',
        className,
      )}
    >
      {/* Shared decorative navy backdrop — matches the hero / statement screens. */}
      <NavyAtmosphere />

      <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
        {/* Invitation panel */}
        <ScrollReveal className="text-center lg:text-left">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold-light uppercase">
            Start your journey
          </p>
          <h2
            id="enquiry-form-heading"
            className="mt-3 font-display text-3xl font-semibold text-white sm:text-4xl lg:text-5xl"
          >
            Request information
          </h2>
          <p className="mx-auto mt-4 max-w-md text-balance text-white/70 lg:mx-0">
            Tell us a little about yourself and what you would like to study — our team is
            happy to talk through your options and answer any questions along the way.
          </p>

          {/* Brand promise as three quiet reassurances. */}
          <ul className="mx-auto mt-8 flex max-w-md flex-col gap-3 text-left lg:mx-0">
            {brand.promise.map((line) => (
              <li key={line} className="flex items-center gap-3">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold-light">
                  <Check aria-hidden="true" className="size-3.5" strokeWidth={2.5} />
                </span>
                <span className="text-sm text-white/85">{line}</span>
              </li>
            ))}
          </ul>
        </ScrollReveal>

        {/* Form card */}
        <ScrollReveal className="rounded-2xl bg-background p-6 shadow-2xl sm:p-8">
          {submitted ? (
            <div className="flex flex-col items-center py-10 text-center">
              <CheckCircle2
                aria-hidden="true"
                className="size-12 text-brand-gold"
                strokeWidth={1.5}
              />
              <h3 className="mt-4 font-display text-2xl font-semibold text-foreground">
                Thank you
              </h3>
              <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                We&apos;ve received your enquiry and a member of the IBAS team will be in touch
                with you shortly.
              </p>
            </div>
          ) : (
            <form noValidate onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">
                  First name
                  <RequiredMark />
                </Label>
                <Input type="text" id="firstName" name="firstName" required autoComplete="given-name" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lastName">
                  Last name
                  <RequiredMark />
                </Label>
                <Input type="text" id="lastName" name="lastName" required autoComplete="family-name" />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="email">
                  Email address
                  <RequiredMark />
                </Label>
                <Input type="email" id="email" name="email" required autoComplete="email" />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="phone">Phone / mobile number</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  autoComplete="tel"
                  placeholder="Include your country code"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="country">Which country are you located in?</Label>
                <Input type="text" id="country" name="country" autoComplete="country-name" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="programme">Study programme of interest</Label>
                <div className="relative">
                  <select
                    id="programme"
                    name="programme"
                    defaultValue=""
                    className={cn(FIELD_CLASSES, 'h-9 appearance-none pr-8')}
                  >
                    <option value="">Select a programme</option>
                    {PROGRAMME_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
                  />
                </div>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="mode">Which mode of study do you prefer?</Label>
                <div className="relative">
                  <select
                    id="mode"
                    name="mode"
                    defaultValue=""
                    className={cn(FIELD_CLASSES, 'h-9 appearance-none pr-8')}
                  >
                    <option value="">Select a mode</option>
                    {MODE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    aria-hidden="true"
                    className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
                  />
                </div>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="message">How can we help you?</Label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  className={cn(FIELD_CLASSES, 'h-auto resize-none py-2')}
                />
              </div>

              <div className="sm:col-span-2">
                <Button type="submit" variant="secondary" size="lg" className="w-full">
                  <Send aria-hidden="true" className="size-4" />
                  Send enquiry
                </Button>
              </div>
            </form>
          )}
        </ScrollReveal>
      </div>
    </section>
  );
}
